import React, { useEffect, useState } from 'react';
import SideNavigationBar from '../components/SideNavigationBar';
import {
	Grid,
	Typography,
	CircularProgress,
	Box,
	TableContainer,
	Table,
	TableBody,
	TableRow,
	TableCell,
	TextField,
	Button,
	Menu,
	MenuItem,
} from '@mui/material';
import {
	FileUploadDiv,
	helperText,
	useMainCustomStyle,
} from '../assets/theme/Theme';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SearchDropDown from '../components/SearchDropDown';
import { v4 as uuidv4 } from 'uuid';
import {
	getEnabledUserFeatures,
	getOptimizedCarePlan,
	getOptimizedPlanData,
} from '../context/MainProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import UserLimitationPopup from '../components/UserLimitationPopup';
import { FEATURE_TYPES } from '../Utils/Enums';
import {
	getAccessToken,
	getElderHomeId,
	getUserEmail,
	getUserId,
	getUserName,
} from '../Utils/AuthUtil';
import {
	addOptimizedCarePlan,
	fetchInstructionDescription,
	generateNotificationData,
	getResidentIds,
	updateCarePlan,
	updateExternalPlanOptimizedFlag,
} from '../services/ElderService';
import { StarIcon, UploadIcon } from '../assets/icons/Icons';
import ToastBanner from '../components/ToastBanner';
import IconButton from '../components/IconButton';
import AlertBanner from '../components/AlertBanner';
import Dropzone from 'react-dropzone';
import FileContentTile from '../components/FileContentTile';

// eslint-disable-next-line no-undef
const central_db_host = process.env.REACT_APP_UMS_API_URL;

const TEMPLATES_OLD = [
	// Change in Registered Details
	{
		id: 'change_in_registered_details.provider_or_manager',
		type: 'change_in_registered_details',
		displayName: 'Change in Registered Details > Provider or Manager',
	},
	{
		id: 'change_in_registered_details.statement_of_purpose_or_address',
		type: 'change_in_registered_details',
		displayName:
			'Change in Registered Details > Statement of Purpose or Address',
	},

	// Events & Incidents
	{
		id: 'events_and_incidents.absence_of_registered_individual',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Absence of Registered Individual',
	},
	{
		id: 'events_and_incidents.allegations_of_abuse',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Allegations of Abuse',
	},
	{
		id: 'events_and_incidents.child_on_adult_psychiatric_ward',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Child on Adult Psychiatric Ward',
	},
	{
		id: 'events_and_incidents.death_of_person_detained_under_mha',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Death of Person Detained Under MHA',
	},
	{
		id: 'events_and_incidents.death_of_person_using_service',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Death of Person Using Service',
	},
	{
		id: 'events_and_incidents.death_of_registered_provider',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Death of Registered Provider',
	},
	{
		id: 'events_and_incidents.dols_outcome',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > DoLS Outcome',
	},
	{
		id: 'events_and_incidents.events_stop_service_running',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Events Stopping Service Operation',
	},
	{
		id: 'events_and_incidents.liquidator_or_trustee_plans',
		type: 'events_and_incidents',
		displayName: "Events & Incidents > Liquidator or Trustee's Plans",
	},
	{
		id: 'events_and_incidents.police_involvement',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Police Involvement',
	},
	{
		id: 'events_and_incidents.return_of_registered_individual',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Return of Registered Individual',
	},
	{
		id: 'events_and_incidents.serious_injury',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Serious Injury',
	},
	{
		id: 'events_and_incidents.unauthorised_absence_mha',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Unauthorised Absence (MHA)',
	},
];

const TEMPLATES = [
	{
		id: 'events_and_incidents.absence_of_registered_individual',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Absence of Registered Individual',
		instruction:
			'Please describe the absence/incident in detail, including the start date, reason, who will take over responsibilities, full details of the appointed person, and any additional information.',
	},
	{
		id: 'events_and_incidents.return_of_registered_individual',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Return of Registered Individual',
		instruction:
			'Please describe the return from absence, including return date, original reference, and any additional details.',
	},
	{
		id: 'events_and_incidents.events_stop_service_running',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Events Stopping Service Operation',
		instruction:
			'Please describe the event in detail, including the type of event, start date and time, reason or cause, impact on residents, staff, and service delivery, immediate actions taken, temporary arrangements made to keep the service running safely, details of any appointed person taking over responsibilities, and any further relevant information such as ongoing risks, external notifications, next steps, or preventive measures.',
	},
	{
		id: 'events_and_incidents.serious_injury',
		type: 'events_and_incidents',
		displayName: 'Events & Incidents > Serious Injury',
		instruction:
			'Please describe the serious injury incident in detail, including the date and time it occurred, where it happened, how the person was injured, the type and severity of injury, whether the person was already known to be at risk, what risk assessments or support measures were in place, immediate actions taken after the incident, medical treatment provided, whether family/relevant persons or authorities were informed, steps taken to reduce further risk, and any additional relevant information. Please use a unique identifier/code for the injured person and do not include their name.',
	},
];

const providerDetails = {
	providerNotificationReference: 'REF-ABS-2026-0018',
	providerName: 'Northfield Care Services Ltd',
	cqcProviderNumber: '1-0001234567',
	formFilledBy: '',
	dateSubmitted: '23/04/2026',
	contactForMoreInformation: 'Daniel Mercer',
	telephoneNumber: '01632 960421',
	emailAddress: '',
	affectedLocationScope: 'one_location',
	locationNameAndAddress:
		'Northfield Residential Home, 14 Willow Park Road, Ashford',
	locationPostcode: 'AS1 4PL',
	cqcLocationNumber: '1-0007654321',
};

const initialErrorState = { status: false, msg: '' };

function NotificationSending() {
	const navigate = useNavigate();

	const mainDivStyle = useMainCustomStyle();
	const userId = getUserId();
	const homeId = getElderHomeId();
	const token = getAccessToken();
	const [id, setId] = useState(null);
	const [residentIds, setResidentIds] = useState([]);
	const [idError, setIdError] = useState(initialErrorState);
	const [tempError, setTempError] = useState(initialErrorState);
	const [templates, setTemplates] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [tempInstructions, setTempInstructions] = useState([]);
	const [onShowTemplateInstruction, setOnShowTemplateInstruction] =
		useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { enabledUserFeatureList } = getEnabledUserFeatures();
	const isThisFeatureDisabled = !enabledUserFeatureList.includes(
		FEATURE_TYPES[3].key,
	);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [showNoResidentBanner, setShowNoResidentBanner] = useState(false);
	const [fileData, setFileData] = useState([]);
	const [txtError, setTxtError] = useState(initialErrorState);
	const [anchorEl, setAnchorEl] = useState(null);
	const [instructions, setInstructions] = useState('');
	const [instructionError, setInstructionError] = useState(initialErrorState);

	useEffect(() => {
		const token = getAccessToken();
		fetchResidentIds(token);
		const templateNames = TEMPLATES.map(item => item.displayName);
		setTemplates(templateNames);
	}, []);

	async function fetchResidentIds() {
		const allResResponse = await getResidentIds(homeId, token);
		const response = await getResidentIds(homeId, token);

		if (allResResponse.message === 'Success') {
			setShowNoResidentBanner(
				allResResponse.content.length > 0 ? false : true,
			);
		}

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);

			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
		} else {
			toast.error(response.message);
		}
	}

	function validateUploadInputs() {
		let isValid = true;

		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select Resident' });
			isValid = false;
		}

		if (!selectedTemplate || selectedTemplate === '') {
			setTempError({ status: true, msg: 'Select Template' });
			isValid = false;
		}

		return isValid;
	}

	async function fetchTemplateData(docType) {
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const template = TEMPLATES.find(
			item => item.displayName === selectedTemplate,
		);

		const d = new Date();
		const formatted = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

		const providerObj = {
			...providerDetails,
			formFilledBy: getUserName(),
			emailAddress: getUserEmail(),
			dateSubmitted: formatted,
		};
		const provider = Object.values(providerObj).join('\n');

		const formData = new FormData();
		formData.append('elder_id', selectedElder.id);
		formData.append('notification_category', template.id);
		formData.append('client_id', homeId);
		formData.append('return_download_docx', true);
		formData.append('file_format', docType);
		formData.append('input_text', instructions);
		formData.append('provider_details', provider);
		fileData.forEach(file => {
			formData.append('uploaded_files', file);
		});

		return generateNotificationData(formData, docType);
	}

	async function onFetchTemplateData(docType = 'docx') {
		if (!validateUploadInputs()) return;

		if (!instructions || instructions === '') {
			setInstructionError({
				status: true,
				msg: 'Please Provide Details',
			});
			return;
		}

		setAnchorEl(null);
		setIsLoading(true);

		try {
			const { blob, filename } = await fetchTemplateData(docType);

			downloadFile(blob, filename);

			setFileData([]);
			setId('');
			setSelectedTemplate('');
			setInstructions('');
			setTempInstructions([]);
			setOnShowTemplateInstruction(false);

			toast.success('Document downloaded!');
		} catch (error) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
		}
	}

	async function generateBothTempData() {
		if (!validateUploadInputs()) return;

		if (!instructions || instructions === '') {
			setInstructionError({
				status: true,
				msg: 'Please Provide Details',
			});
			return;
		}

		setAnchorEl(null);
		setIsLoading(true);

		try {
			const pdf = await fetchTemplateData('pdf');
			downloadFile(pdf.blob, pdf.filename);

			const docx = await fetchTemplateData('docx');
			downloadFile(docx.blob, docx.filename);

			setFileData([]);
			setId('');
			setSelectedTemplate('');
			setInstructions('');
			setTempInstructions([]);
			setOnShowTemplateInstruction(false);

			toast.success('Both documents downloaded!');
		} catch (error) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
		}
	}

	function downloadFile(blob, filename) {
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = filename;

		document.body.appendChild(a);
		a.click();
		a.remove();

		URL.revokeObjectURL(url);
	}

	function onChangeSelection(e, value) {
		setSelectedTemplate(value);
		setTempError(initialErrorState);
		onFetchInstructions(value);
	}

	function onChangeSelectionElder(e, value) {
		setId(value);
		setIdError(initialErrorState);
	}

	function onChangeInstruction(e) {
		setInstructions(e.target.value);
		setInstructionError(initialErrorState);
	}

	function onFetchInstructions(templateName) {
		const template = TEMPLATES.find(
			item => item.displayName === templateName,
		);

		// const response = fetchInstructionDescription(template.id);
		// if (!response.error) {
		setTempInstructions(template.instruction);
		setOnShowTemplateInstruction(true);
		// } else {
		// 	toast.error('Unable to fetch instructions.');
		// }
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0].size && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData([...fileData, e[0]]);
	}

	function onRemoveFiles(content) {
		const clone = fileData.filter(value => value.name !== content.name);
		setFileData(clone);
	}

	function handleClose() {
		setAnchorEl(null);
	}

	const textFieldStyle = {
		'& .MuiOutlinedInput-root': {
			fontSize: '14px',
			height: '40px',
			// Hide border by default
			'& fieldset': {
				border: 'none',
			},
			// Show border only on hover
			'&:hover fieldset': {
				border: '1px solid #e0e0e0',
			},
			// Keep focus border visible
			'&.Mui-focused fieldset': {
				border: '2px solid #1976d2', // or your preferred focus color
			},
		},
	};

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid
					container
					paddingLeft={{ xs: '0px', md: '20px', sm: '20px' }}
					paddingRight={'20px'}>
					<Grid item paddingLeft="30px" paddingBottom={'20px'}>
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Send Notification
						</Typography>
					</Grid>
					{showNoResidentBanner && (
						<Grid
							container
							sx={{
								borderRadius: '5px',
								paddingLeft: '30px',
								paddingBottom: '10px',
							}}>
							<AlertBanner
								content={
									'No residents available. Add residents to get started.'
								}
								btnOneTxt={'Manual Onboarding'}
								btnTwoTxt={'Connect Api'}
								handleButtonOneClick={() =>
									navigate('/addResident')
								}
								handleButtonTwoClick={() =>
									navigate('/integration')
								}
								displayBtnOne={true}
								displayBtnTwo={true}
							/>
						</Grid>
					)}
					<Grid
						container
						marginLeft={'30px'}
						bgcolor={'#FFFFFF'}
						borderRadius={'10px'}
						flexDirection={'column'}>
						<Grid container paddingLeft={'20px'}>
							<Grid
								item
								xs={12}
								sm={4}
								paddingRight={'10px'}
								paddingTop={'20px'}>
								<Typography
									fontSize={'16px'}
									color={'#7C7C7C'}
									paddingBottom="10px">
									Resident{' '}
									<span style={{ color: 'red' }}>*</span>
								</Typography>
								<Box
									display="flex"
									alignItems="center"
									width="100%">
									<SearchDropDown
										value={id}
										dropDownData={residentIds}
										onChange={onChangeSelectionElder}
										placeholder={'Select Elder Id here...'}
										error={idError.status}
										disable={isLoading}
									/>
								</Box>
								<Typography style={helperText}>
									{idError.msg}
								</Typography>
							</Grid>
							<Grid
								item
								xs={12}
								sm={4}
								paddingRight={'10px'}
								paddingTop={'20px'}
								paddingBottom={'5px'}>
								<Typography
									fontSize={'16px'}
									color={'#7C7C7C'}
									paddingBottom="10px">
									Templates{' '}
									<span style={{ color: 'red' }}>*</span>
								</Typography>
								<Box
									display="flex"
									alignItems="center"
									width="100%">
									<SearchDropDown
										value={selectedTemplate}
										dropDownData={templates}
										onChange={onChangeSelection}
										placeholder={'Select template here...'}
										error={tempError.status}
										fullWidth
										disable={isLoading}
									/>
								</Box>
								<Typography style={helperText}>
									{tempError.msg}
								</Typography>
							</Grid>
						</Grid>
						{onShowTemplateInstruction && (
							<Grid
								container
								paddingBottom={'10px'}
								paddingRight={'10px'}
								paddingLeft={'20px'}>
								<Grid
									container
									sx={{
										backgroundColor: '#FAFAFA',
										marginBottom: '8px',
										padding: '16px 8px 16px',
										flexDirection: 'column',
										display: 'flex',
									}}>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: 1,
										}}>
										<Typography
											component="div"
											sx={{
												fontSize: '14px',
												color: '#324054',
												fontWeight: 500,
											}}>
											{tempInstructions}
											<span style={{ color: 'red' }}>
												*
											</span>
										</Typography>
									</Box>
								</Grid>
								<Grid container>
									<TextField
										fullWidth
										multiline
										rows={8}
										value={instructions}
										sx={{
											'& .MuiInputBase-input': {
												fontSize: '14px',
											},
										}}
										onChange={onChangeInstruction}
										placeholder="Provide above details here..."
										error={instructionError.status}
										InputProps={{
											readOnly: isLoading,
										}}
									/>
									{instructionError.status && (
										<Typography sx={helperText}>
											{instructionError.msg}
										</Typography>
									)}
								</Grid>
							</Grid>
						)}
						<Grid container paddingX={'10px'} marginX={'10px'}>
							<Dropzone
								disabled={isLoading}
								onDrop={acceptedFiles =>
									readTextFile(acceptedFiles)
								}>
								{({ getRootProps, getInputProps }) => (
									<section style={{ width: '100%' }}>
										<div
											{...getRootProps()}
											style={{
												...FileUploadDiv,
												border: txtError?.status
													? '1px dashed red'
													: FileUploadDiv.border,
												marginRight: '10px',
											}}>
											<input {...getInputProps()} />
											<UploadIcon />
											<br />
											<>
												<Typography
													fontSize={'16px'}
													fontWeight={'700'}
													marginTop={'5px'}
													color={'#7C7C7C'}>
													{`Please attach files for document generation`}
												</Typography>
												<Typography
													fontSize={'12px'}
													color={'#B4B2B2'}>
													Supports files up to 5mb
												</Typography>
											</>
										</div>
									</section>
								)}
							</Dropzone>
							<Typography sx={helperText}>
								{txtError.msg}
							</Typography>
							{fileData && (
								<Grid container flexDirection={'column'}>
									<Grid
										container
										alignItems="center"
										spacing={2}>
										{fileData.map((item, index) => {
											return (
												<Grid
													item
													xs={6}
													sm={4}
													md={3}
													key={index}>
													<FileContentTile
														key={index}
														file={item}
														onRemoveFiles={
															onRemoveFiles
														}
														disableCloseBtn={
															isLoading
														}
													/>
												</Grid>
											);
										})}
									</Grid>
								</Grid>
							)}
						</Grid>
						<Grid
							container
							padding={'10px'}
							justifyContent={'flex-end'}>
							<IconButton
								text={'Generate'}
								onClick={e => setAnchorEl(e.currentTarget)}
								disabled={isLoading}
							/>
							<Menu
								anchorEl={anchorEl}
								open={Boolean(anchorEl)}
								onClose={handleClose}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'right',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
								PaperProps={{
									sx: {
										mt: 0,
										width: '180px',
									},
								}}>
								<MenuItem
									sx={{ fontSize: '14px' }}
									onClick={() => onFetchTemplateData('pdf')}>
									Generate (.PDF)
								</MenuItem>
								<MenuItem
									sx={{ fontSize: '14px' }}
									onClick={() => onFetchTemplateData('docx')}>
									Generate (.DOCX)
								</MenuItem>
								<MenuItem
									sx={{ fontSize: '14px' }}
									onClick={generateBothTempData}>
									Generate Both
								</MenuItem>
							</Menu>
						</Grid>
					</Grid>
				</Grid>
			</div>
			{isLoading && (
				<ToastBanner
					open={isLoading}
					message={'Generating Document...'}
					icon={<StarIcon color={'#FFFFFF'} />}
					isCompleted={isGenerationComplete}
				/>
			)}
		</>
	);
}

export default NotificationSending;
